import React, { useMemo } from "react";
import { Bar } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisLeft } from "@visx/axis";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Text, getStringWidth } from "@visx/text";
import { useTheme } from "@mui/material/styles";

const getCategory = (d) => d.category;
const getCategoryFrequency = (d) => d.frequency;

const defaultMargin = { top: 0, right: 8, bottom: 0, left: 8 };

const AXIS_LABEL_MAX_WIDTH = 250;
const AXIS_LABEL_GAP = 12;
const LABEL_STYLE = { fontSize: 11 };

const measure = (s) => getStringWidth(s, LABEL_STYLE) ?? s.length * 7;

const wrapLineCount = (label, avail) => {
  const words = label.split(/\s+/).filter(Boolean);
  let lines = 1;
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (!cur || measure(test) <= avail) {
      cur = test;
    } else {
      cur = w;
      lines += 1;
    }
  }
  return lines;
};

function StatsByCategory({ data, width, height, margin = defaultMargin }) {
  const theme = useTheme();
  const barFill = theme.palette.primary.light;
  const barLabelFill = theme.palette.primary.dark;
  const barLabelFillAlt = theme.palette.primary.contrastText;
  const axisColor = theme.palette.text.primary;

  const leftAxisWidth = useMemo(() => {
    const labels = data.map((d) => String(getCategory(d)));
    if (!labels.length) return AXIS_LABEL_GAP;

    let lo = Math.ceil(
      Math.max(...labels.flatMap((l) => l.split(/\s+/).map(measure))),
    );
    let hi = Math.ceil(Math.max(...labels.map(measure)));
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (labels.every((l) => wrapLineCount(l, mid) <= 2)) hi = mid;
      else lo = mid + 1;
    }
    return Math.min(AXIS_LABEL_MAX_WIDTH, lo + AXIS_LABEL_GAP);
  }, [data]);

  const xMax = Math.max(width - margin.left - margin.right - leftAxisWidth, 0);
  const yMax = Math.max(height - margin.top - margin.bottom, 0);

  const yScale = useMemo(
    () =>
      scaleBand({
        range: [0, yMax],
        round: true,
        domain: data.map(getCategory),
        padding: 0.1,
      }),
    [yMax],
  );

  const xScale = useMemo(
    () =>
      scaleLinear({
        range: [0, xMax],
        round: true,
        domain: [0, Math.max(...data.map(getCategoryFrequency))],
      }),
    [xMax],
  );

  return (
    <svg width={width} height={height}>
      <Group top={margin.top} left={margin.left + leftAxisWidth}>
        {data.map((d) => {
          const category = getCategory(d);
          const frequency = getCategoryFrequency(d);

          const barWidth = yScale.bandwidth();
          const barHeight = Math.max(xScale(frequency || 0) ?? 0, 0);
          const barY = yScale(category) || 0;
          const altLabelPosition = barHeight + 5 >= xMax;

          return (
            <Group key={`bar-${category}`}>
              <Bar
                x={0}
                y={barY}
                width={barHeight}
                height={barWidth}
                fill={barFill}
              />
              <text
                x={
                  altLabelPosition
                    ? xMax - 15
                    : xScale(frequency) + margin.left + margin.right
                }
                y={barY + 5 + yScale.bandwidth() / 2}
                fontSize={frequency > 99 ? 10 : 12}
                fill={altLabelPosition ? barLabelFillAlt : barLabelFill}
                textAnchor="middle"
              >
                {frequency}
              </text>
            </Group>
          );
        })}
      </Group>
      <AxisLeft
        top={margin.top}
        left={margin.left + leftAxisWidth}
        scale={yScale}
        stroke={axisColor}
        tickStroke={axisColor}
        tickComponent={({ x, y, formattedValue }) => (
          <Text
            x={x}
            y={y}
            width={leftAxisWidth - AXIS_LABEL_GAP}
            verticalAnchor="middle"
            textAnchor="end"
            fontSize={11}
            fill={axisColor}
          >
            {formattedValue}
          </Text>
        )}
      />
    </svg>
  );
}

export default StatsByCategory;
